import { useState } from 'react';
import { toast } from 'react-toastify';
import { Plus, X, Lightbulb, Trash2 } from 'lucide-react';
import { Button } from '@components/ui';
import clsx from 'clsx';

const CATEGORIES = ['Yeni Özellik', 'Hata Düzeltme', 'İyileştirme', 'Entegrasyon'];

const PRIORITIES = [
    { key: 'dusuk', label: 'Düşük', className: 'bg-tertiary/10 text-tertiary' },
    { key: 'orta', label: 'Orta', className: 'bg-warning/10 text-warning' },
    { key: 'yuksek', label: 'Yüksek', className: 'bg-error/10 text-error' },
    { key: 'kritik', label: 'Kritik', className: 'bg-error text-white' },
];

const STATUSES = [
    { key: 'inceleniyor', label: 'İnceleniyor', className: 'bg-info/10 text-info' },
    { key: 'planlandi', label: 'Planlandı', className: 'bg-primary/10 text-primary' },
    { key: 'gelistiriliyor', label: 'Geliştiriliyor', className: 'bg-warning/10 text-warning' },
    { key: 'tamamlandi', label: 'Tamamlandı', className: 'bg-success/10 text-success' },
    { key: 'reddedildi', label: 'Reddedildi', className: 'bg-error/10 text-error' },
];

const mockRequests = [
    {
        id: 1,
        title: 'E-Fatura entegrasyonu',
        category: 'Entegrasyon',
        priority: 'yuksek',
        status: 'planlandi',
        date: '2026-01-15',
        description: 'GİB e-fatura sistemiyle entegrasyon sağlanması',
    },
    {
        id: 2,
        title: 'Vadesi gelen ödemeler için bildirim',
        category: 'Yeni Özellik',
        priority: 'orta',
        status: 'inceleniyor',
        date: '2026-01-20',
        description: 'Taksit ve çek vadeleri yaklaştığında push bildirim gönderilmesi',
    },
    {
        id: 3,
        title: 'Taksit matrisi Excel export',
        category: 'İyileştirme',
        priority: 'dusuk',
        status: 'tamamlandi',
        date: '2026-02-01',
        description: 'Taksit matrisinin Excel olarak dışa aktarılması',
    },
    {
        id: 4,
        title: 'Çek vadesi hatırlatma',
        category: 'Yeni Özellik',
        priority: 'yuksek',
        status: 'gelistiriliyor',
        date: '2026-02-10',
        description: 'Vadeye 3 gün kalan çekler için otomatik uyarı',
    },
];

const getPriority = (key) => PRIORITIES.find(p => p.key === key);
const getStatus = (key) => STATUSES.find(s => s.key === key);

export default function FeatureRequests() {
    const [requests, setRequests] = useState(mockRequests);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        title: '',
        category: 'Yeni Özellik',
        priority: 'orta',
        description: '',
    });

    const handleAdd = () => {
        if (!form.title.trim()) {
            toast.error('Talep başlığı girin');
            return;
        }
        const newRequest = {
            id: Date.now(),
            ...form,
            status: 'inceleniyor',
            date: new Date().toISOString().split('T')[0],
        };
        setRequests(prev => [newRequest, ...prev]);
        setForm({ title: '', category: 'Yeni Özellik', priority: 'orta', description: '' });
        setShowModal(false);
        toast.success('Geliştirme talebi oluşturuldu');
    };

    const handleDelete = (id) => {
        setRequests(prev => prev.filter(r => r.id !== id));
        toast.success('Talep silindi');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-text-primary">Geliştirme Talepleri</h2>
                    <p className="text-text-tertiary text-sm mt-1">
                        Özellik isteklerinizi ve hata bildirimlerinizi buradan iletin
                    </p>
                </div>
                <Button
                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={() => setShowModal(true)}
                    className="w-full sm:w-auto"
                >
                    Talep Ekle
                </Button>
            </div>

            {/* Tablo */}
            <div className="bg-bg-tertiary/30 rounded-xl border border-border-light overflow-hidden">
                <div className="px-5 py-4 border-b border-border-light flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Lightbulb size={20} className="text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-text-primary">Tüm Talepler</h3>
                        <p className="text-sm text-text-tertiary">{requests.length} talep</p>
                    </div>
                </div>

                {/* Desktop Tablo */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border-light bg-bg-secondary/50">
                                <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Talep</th>
                                <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Kategori</th>
                                <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Öncelik</th>
                                <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Durum</th>
                                <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Tarih</th>
                                <th className="text-right px-5 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-light">
                            {requests.map((req) => {
                                const priority = getPriority(req.priority);
                                const status = getStatus(req.status);
                                return (
                                    <tr key={req.id} className="hover:bg-bg-secondary/40 transition-colors">
                                        <td className="px-5 py-4">
                                            <p className="font-medium text-text-primary">{req.title}</p>
                                            {req.description && (
                                                <p className="text-xs text-text-muted mt-0.5 max-w-xs truncate">{req.description}</p>
                                            )}
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className="text-sm text-text-secondary">{req.category}</span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', priority?.className)}>
                                                {priority?.label}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', status?.className)}>
                                                {status?.label}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className="text-sm text-text-tertiary">
                                                {new Date(req.date).toLocaleDateString('tr-TR')}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <button
                                                onClick={() => handleDelete(req.id)}
                                                className="p-1.5 rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {requests.length === 0 && (
                        <p className="text-center text-text-muted py-10 text-sm">Henüz talep bulunmuyor.</p>
                    )}
                </div>

                {/* Mobile Kart Görünümü */}
                <div className="md:hidden divide-y divide-border-light">
                    {requests.map((req) => {
                        const priority = getPriority(req.priority);
                        const status = getStatus(req.status);
                        return (
                            <div key={req.id} className="p-4 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="font-medium text-text-primary text-sm">{req.title}</p>
                                    <button
                                        onClick={() => handleDelete(req.id)}
                                        className="p-1.5 rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors shrink-0"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-xs text-text-secondary bg-bg-secondary px-2 py-0.5 rounded">{req.category}</span>
                                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', priority?.className)}>{priority?.label}</span>
                                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', status?.className)}>{status?.label}</span>
                                </div>
                                <p className="text-xs text-text-muted">{new Date(req.date).toLocaleDateString('tr-TR')}</p>
                            </div>
                        );
                    })}
                    {requests.length === 0 && (
                        <p className="text-center text-text-muted py-8 text-sm">Henüz talep bulunmuyor.</p>
                    )}
                </div>
            </div>

            {/* Talep Ekleme Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="bg-bg-secondary rounded-t-2xl sm:rounded-2xl border border-border-light p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-bold text-text-primary">Yeni Talep</h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 rounded-lg text-text-muted hover:bg-bg-tertiary transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1.5">Talep Başlığı *</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="Özellik veya hata açıklaması..."
                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary placeholder:text-text-muted focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Kategori</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        className="w-full px-3 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer text-sm"
                                    >
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Öncelik</label>
                                    <select
                                        value={form.priority}
                                        onChange={(e) => setForm({ ...form, priority: e.target.value })}
                                        className="w-full px-3 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all cursor-pointer text-sm"
                                    >
                                        {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1.5">Açıklama</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Detaylı açıklama (isteğe bağlı)..."
                                    rows={3}
                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-bg-primary text-text-primary placeholder:text-text-muted focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
                            <Button variant="ghost" className="flex-1" onClick={() => setShowModal(false)}>İptal</Button>
                            <Button variant="primary" className="flex-1" icon={<Plus size={18} />} onClick={handleAdd}>
                                Talep Oluştur
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
